<script setup lang="ts">
import type { TodosSubmissionsStatus } from "@profile-service-types/index.ts";
import Dropdown, { type DropdownChangeEvent } from "primevue/dropdown";
import { computed } from "vue";
import type { DisplayableStatus } from "@/components/MyStudents/Submissions/statusMaps";
import {
  getStatusOptions,
  getZeroStateStatusOptions,
  STATUS_COLOR_CLASSES,
  STATUS_DISPLAY,
} from "@/components/MyStudents/Submissions/statusMaps";
import CheckboxCheckmark from "@/components/shared/icons/CheckboxCheckmark.vue";
import { useTenantTheming } from "@/composables/useTenantTheming.ts";
import { statusSelectPt } from "./styles";

const props = withDefaults(
  defineProps<{
    modelValue: DisplayableStatus | undefined;
    permittedStatuses?: TodosSubmissionsStatus[];
    disabled?: boolean;
    zeroState?: boolean;
    dataTestid?: string;
  }>(),
  {
    permittedStatuses: () => [],
    disabled: false,
    zeroState: false,
    dataTestid: "my-students-status-select",
  },
);

const emit = defineEmits<(e: "update:modelValue", value: TodosSubmissionsStatus) => void>();

const { hexColors } = useTenantTheming();

const options = computed<TodosSubmissionsStatus[]>(() =>
  props.zeroState ? getZeroStateStatusOptions(props.permittedStatuses) : getStatusOptions(props.permittedStatuses),
);

const displayLabel = (status: DisplayableStatus | undefined): string => (status ? STATUS_DISPLAY[status] : "");

const dotClass = (status: DisplayableStatus | undefined): string =>
  status ? STATUS_COLOR_CLASSES[status].dot : "bg-gray-475";

const valueTextClass = computed<string>(() => {
  if (props.disabled) {
    return "text-gray-600";
  }
  return props.modelValue ? STATUS_COLOR_CLASSES[props.modelValue].text : "text-indigo-base";
});

const onChange = (event: DropdownChangeEvent): void => {
  if (event.value && event.value !== props.modelValue) {
    emit("update:modelValue", event.value);
  }
};
</script>

<template>
  <Dropdown
    :model-value="modelValue"
    :options="options"
    :disabled="disabled"
    :pt="statusSelectPt"
    :data-testid="dataTestid"
    @change="onChange"
  >
    <template #value="{ value }">
      <span class="flex items-center gap-2 text-sm font-bold" :class="valueTextClass">
        <span class="w-2.5 h-2.5 rounded-full shrink-0" :class="dotClass(value)" />
        {{ displayLabel(value) }}
      </span>
    </template>
    <template #option="{ option }">
      <span class="flex items-center gap-3 w-full" :data-testid="`my-students-status-option-${option}`">
        <span class="w-2.5 h-2.5 rounded-full shrink-0" :class="dotClass(option)" />
        <span class="flex-1">{{ displayLabel(option) }}</span>
        <CheckboxCheckmark v-if="option === modelValue" class="w-4 h-4 shrink-0" :rgb-fill-color="hexColors.secondary" />
      </span>
    </template>
  </Dropdown>
</template>
